import React, { useId, useLayoutEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { toast } from 'react-toastify'

import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import LockIcon from '@mui/icons-material/Lock'

import Button from '../Common/Button'

import { Product } from '@/src/types/api-types'
import { ProductsBasket } from '@/type/basket'
import { useRestMutation } from '@/lib/hook/tanstackQuery'
import { ApiMutationEndpoint } from '@/src/ApiMutationEndpoint'
import getProductAvailable from '@/lib/getProductAvailable'
import ProductAvaibleAfter from '@/components/Product/ProductAvaibleAfter'
import InformationPeremption from '@/components/Product/InformationPeremption'
import PromotionPastille from '@/components/Product/PromotionPastille'
import getProductPrice from '@/lib/getProductPrice'
import OrderButtonAddProductToBasket from '../Orders/OrderButtonAddProductToBasket'
import { Tooltip } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import Card from '../Common/Card'
import clsx from 'clsx'

type Props = {
  product: Product | ProductsBasket
  declinationId?: string
  qtyCart?: number
  list?: boolean
  compactMode?: boolean
  compact?: boolean
}

const ProductAddToBasket: React.FC<Props> = ({
  qtyCart = 0,
  product,
  list = false,
  compactMode = false,
  compact = false,
}) => {
  const id = useId()
  const [showInfoIcon, setShowInfoIcon] = useState<boolean>(false)
  const [notificationSent, setNotificationSent] = useState<boolean>(false)

  useLayoutEffect(() => {
    const element = document.getElementById(id)
    if (element) {
      const hasContent = element.textContent?.trim().length || element.children.length > 0

      if (hasContent) setShowInfoIcon(true)
    }
  }, [id])

  // Demande de notification de retour en stock. Body CS-Cart : { input: { product_id } }
  // (reproduit l'ancien resolver addProductSubscription -> 'AskStockProduct').
  const { mutate: addProductSubscription } = useRestMutation<any, Error, { input: { product_id: string } }>({
    ...ApiMutationEndpoint.CATALOG.ADD_PRODUCT_SUBSCRIPTION,
    onSuccess: () => {
      setNotificationSent(true)
      toast.success(t({ id: 'web_demande_envoyee_notif_stock' }))
    },
    onError: () => {
      toast.error(t({ id: 'web_erreur_envoi_demande_notif_stock' }))
    },
  })

  const handleClickNotify = () => {
    if (notificationSent) return

    addProductSubscription({
      input: {
        product_id: String(product.product_id),
      },
    })
  }

  const isProductAvailable: boolean = getProductAvailable(product)

  const { promotionLimited } = getProductPrice(product, 1)

  if (!isProductAvailable) {
    return (
      <>
        {!list && (
          <Button
            variant={'contained'}
            className="min-w-[250px]"
            disabled
            style={{
              fontSize: 11,
              lineHeight: 1.2,
            }}
          >
            {product.restricted_unavailable ? (
              <div className="flex items-center justify-center">
                <FormattedMessage id={'web_produit_restricted_unavailable'} />
                <LockIcon className="ml-4" />
              </div>
            ) : (
              <FormattedMessage id={'web_produit_indisponible'} />
            )}
          </Button>
        )}
        {!list && <br />}
        {list ? (
          <Tooltip
            title={t({
              id: product.restricted_unavailable
                ? 'web_produit_restricted_unavailable_message'
                : notificationSent
                  ? 'web_produit_indisponible_notif_envoyee'
                  : 'web_produit_indisponible_prevenez_moi',
            })}
            arrow
          >
            <span>
              <Button
                variant={'text'}
                onClick={handleClickNotify}
                disabled={notificationSent || product.restricted_unavailable}
                className="text-sm !min-w-0 !px-1"
              >
                <NotificationsActiveIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            variant={'text'}
            onClick={handleClickNotify}
            disabled={notificationSent || product.restricted_unavailable}
          >
            <NotificationsActiveIcon fontSize="medium" />
            &nbsp;&nbsp;
            <FormattedMessage
              id={
                product.restricted_unavailable
                  ? 'web_produit_restricted_unavailable_message'
                  : notificationSent
                    ? 'web_produit_indisponible_notif_envoyee'
                    : 'web_produit_indisponible_prevenez_moi'
              }
            />
          </Button>
        )}
      </>
    )
  }

  const moreInfos = (
    <>
      {/* Sur un produit precommande, le bloc date + "uniquement en precommande" est porte
          par ProductTableRow / ModernProduct / ProductCard de l'autocomplete. On masque ici pour
          eviter le double affichage de la date. */}
      {product.precommande !== 'Y' && (
        <ProductAvaibleAfter product={product} displayDateAvailable={list} list={list} />
      )}
      {list && <InformationPeremption product={product} style={{ paddingLeft: 0 }} />}
      {list && promotionLimited && (
        <PromotionPastille
          promotionLimited={promotionLimited}
          style={{ marginRight: 8, marginTop: 4, paddingLeft: 0 }}
        />
      )}
    </>
  )

  return (
    <>
      <div
        className={clsx(
          compact
            ? 'flex flex-row items-center gap-1 justify-end w-full'
            : 'flex flex-col justify-center'
        )}
      >
        <div className={'text-center'} data-rec-add>
          <OrderButtonAddProductToBasket
            oneOrder={{
              items: [
                {
                  product_id: product.product_id,
                  product: product.product,
                  amount: qtyCart,
                  available_to_cart: qtyCart > 0,
                },
              ],
            }}
            variant={list ? 'icon' : 'full'}
            buttonProps={
              compact
                ? {
                    size: 'small',
                    sx: {
                      minWidth: 0,
                      px: 1,
                      py: 0.25,
                      fontSize: 11,
                      lineHeight: 1.1,
                      '& .MuiSvgIcon-root': { fontSize: 14 },
                    },
                  }
                : undefined
            }
          />
        </div>

        <div id={id} className="hidden">
          {moreInfos}
        </div>

        <div
          className={clsx(
            !showInfoIcon ? 'hidden' : compact ? 'flex items-center' : 'w-full text-center'
          )}
        >
          {compactMode ? (
            <Tooltip
              title={
                <Card elevate smallRadius className="bg-white">
                  {moreInfos}
                </Card>
              }
              slotProps={{
                tooltip: {
                  className: '!bg-transparent !m-0 !p-0',
                },
              }}
            >
              <InfoIcon
                className={compact ? '' : 'mx-auto'}
                fontSize={compact ? 'small' : 'medium'}
              />
            </Tooltip>
          ) : (
            moreInfos
          )}
        </div>
      </div>
    </>
  )
}

export default ProductAddToBasket
